import { useCallback, useEffect, useMemo, useState } from 'react';
import apiClient from '@src/api/clients';

const normalizeClients = (raw) => {
  if (!raw) {
    return [];
  }

  const list = Array.isArray(raw) ? raw : raw.results || [];

  return list
    .map((client) => ({
      value: client.id,
      label: client.name,
    }))
    .filter((client) => client.value !== undefined && client.label);
};

const sortByLabel = (options) =>
  [...options].sort((a, b) => a.label.localeCompare(b.label));

const BASE_PARAMS = { fields: 'id,name' };

const useClientAutocomplete = ({ onOptionsChange } = {}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const notifyOptionsChange = useCallback(
    (nextOptions) => {
      onOptionsChange?.(nextOptions);
    },
    [onOptionsChange]
  );

  const setOptionsAndNotify = useCallback(
    (next) => {
      setOptions(next);
      notifyOptionsChange(next);
    },
    [notifyOptionsChange]
  );

  const fetchClients = useCallback(
    (rawName = '') => {
      const name = rawName?.trim() || '';
      setLoading(true);
      const requestConfig = {
        url: 'dashboard/clients/',
        method: 'GET',
        payload: { ...BASE_PARAMS },
      };
      if (name) {
        requestConfig.payload.name = name;
      }

      apiClient
        .request(requestConfig)
        .then((res) => {
          const normalized = sortByLabel(normalizeClients(res?.data));
          setOptionsAndNotify(normalized);
        })
        .catch((err) => {
          console.error('Failed to fetch clients', err);
        })
        .finally(() => setLoading(false));
    },
    [setOptionsAndNotify]
  );

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handleInputChange = useCallback(
    (event, value, reason) => {
      if (reason === 'input') {
        fetchClients(value || '');
      }
      if (reason === 'clear') {
        fetchClients('');
      }
    },
    [fetchClients]
  );

  const addOption = useCallback(
    (option) => {
      if (!option || option.value === undefined || !option.label) {
        return;
      }
      setOptions((prev) => {
        if (prev.some((existing) => existing.value === option.value)) {
          return prev;
        }
        const next = sortByLabel([...prev, option]);
        notifyOptionsChange(next);
        return next;
      });
    },
    [notifyOptionsChange]
  );

  const findOptionByValue = useCallback(
    (value) => options.find((option) => option.value === value) || null,
    [options]
  );

  const memoizedOptions = useMemo(() => options, [options]);

  return {
    options: memoizedOptions,
    loading,
    handleInputChange,
    addOption,
    findOptionByValue,
    fetchClients,
  };
};

export default useClientAutocomplete;
